import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { JobService } from '../../../services/job.service';
import { LanguageRoutingService } from '../../../services/language-routing.service';
import { StatusService } from '../../../../../../../libs/shared/src/lib/services/status.service';
import { ConsentService } from '../../../../../../../libs/shared/src/lib/services/consent.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { JobDetailCardComponent, JobDetailCardData } from '../../../components/job-detail-card/job-detail-card.component';
import { AlertCardComponent } from '../../../components/alert-card/alert-card.component';
import { RecommendedJob } from '../../../models/job.model';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-recommended-jobs',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    FormsModule,
    RouterModule,
    JobDetailCardComponent,
    AlertCardComponent,
  ],
  templateUrl: './recommended-jobs.component.html',
  styleUrls: ['./recommended-jobs.component.scss'],
})
export class RecommendedJobsComponent implements OnInit, OnDestroy {
  mappedJobs: JobDetailCardData[] = [];
  isLoading = true;
  error: string | null = null;
  hasAiMatchingConsent = false;
  selectedLimit: number = 10;
  limitOptions = [
    { value: 5, label: '5' },
    { value: 10, label: '10' },
    { value: 15, label: '15' },
    { value: 20, label: '20' },
    { value: 25, label: '25' },
    { value: 30, label: '30' },
  ];
  private destroy$ = new Subject<void>();

  constructor(
    private jobService: JobService,
    private languageRoutingService: LanguageRoutingService,
    private statusService: StatusService,
    private consentService: ConsentService,
    private translocoService: TranslocoService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    // Check consent on every navigation to this route
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.checkConsent();
      });

    // Listen for consent changes
    this.consentService.consentChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.checkConsent();
    });

    // Initial check
    this.checkConsent();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Simple: Check consent and show/hide jobs accordingly
   */
  private checkConsent(): void {
    // Always re-check from localStorage (no caching)
    this.hasAiMatchingConsent = this.consentService.hasAiMatchingConsent();
    if (this.hasAiMatchingConsent) {
      // Has consent: Load jobs
      this.loadRecommendedJobs();
    } else {
      // No consent: Clear jobs and stop loading
      console.log('[RecommendedJobs] No consent, clearing jobs');
      this.mappedJobs = [];
      this.isLoading = false;
      this.error = null;
    }
  }

  loadRecommendedJobs(): void {
    this.isLoading = true;
    this.error = null;

    this.jobService.getRecommendedJobs(this.selectedLimit).subscribe({
      next: (jobs) => {
        this.mappedJobs = this.mapRecommendedJobsToCardData(jobs);
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading recommended jobs:', err);
        this.error = this.translocoService.translate('pages.jobs.recommendedJobs.loadError');
        this.isLoading = false;
      },
    });
  }

  getSettingsRoute(): string {
    return this.languageRoutingService.getLocalizedRoute('/settings');
  }

  private mapRecommendedJobsToCardData(jobs: RecommendedJob[]): JobDetailCardData[] {
    return jobs.map(
      (job, index) =>
        ({
          ...job,
          location: job.placeOfWork,
          isSaved: false, // Not used in recommended mode, but required by interface
          rank: index < 3 ? index + 1 : undefined,
        } as JobDetailCardData)
    );
  }

  onLimitChange(value: string | number): void {
    this.selectedLimit = Number(value);
    this.loadRecommendedJobs();
  }

  getSimilarityScoreClass(score: number): string {
    if (score >= 0.9) return 'similarity-score-excellent';
    if (score >= 0.8) return 'similarity-score-very-good';
    if (score >= 0.7) return 'similarity-score-good';
    if (score >= 0.6) return 'similarity-score-fair';
    return 'similarity-score-poor';
  }

  formatSalary(salary: RecommendedJob['salary']): string {
    if (!salary || !salary.basicSalary) return 'Not specified';

    // Force EUR for SK locale (audit: currency mix $/€ in same list).
    let salaryText = `€${salary.basicSalary}`;
    if (salary.upperSalary && salary.upperSalary > 0) {
      salaryText += ` - ${salary.upperSalary}`;
    }
    if (salary.period) {
      salaryText += ` / ${salary.period}`;
    }
    return salaryText;
  }

  getLocalizedRoute(path: string): string {
    return this.languageRoutingService.getLocalizedRoute(path);
  }

  /**
   * Get job detail route (path only, without query params)
   */
  getJobDetailRoute(job: RecommendedJob): string {
    return this.getLocalizedRoute(`/job/${job.id}`);
  }

  /**
   * Get query params for job detail navigation
   * Includes matchScore when coming from recommended jobs
   */
  getJobQueryParams(job: RecommendedJob): { [key: string]: any } {
    if (job.matchScore !== undefined && job.matchScore !== null) {
      return { matchScore: job.matchScore };
    }
    return {};
  }

  getJobRankClass(index: number): string {
    if (index === 0) return 'job-rank-first';
    if (index === 1) return 'job-rank-second';
    if (index === 2) return 'job-rank-third';
    return 'job-rank-normal';
  }

  getRankLabel(index: number): string {
    if (index === 0) return '#1 Best Match';
    if (index === 1) return '#2';
    if (index === 2) return '#3';
    return '';
  }

  // Handle save/unsave actions - not used for recommended jobs but required by interface
  onSaveJob(jobId: string): void {
    console.log('Save job action not applicable for recommended jobs:', jobId);
  }

  onUnsaveJob(jobId: string): void {
    console.log('Unsave job action not applicable for recommended jobs:', jobId);
  }
}
